import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, todayStr } from '../lib/format';

/* Pengeluaran satu bulan per kelompok & akun (CoA, 30 Okt 2026).
   Angkanya dari /api/kas/per-akun — fungsi yang SAMA dengan jawaban Hermes,
   jadi menu ini dan Telegram tidak pernah beda. */
const WARNA = {
  operasional: 'bg-danger/10 text-danger',
  karyawan: 'bg-gold/20 text-gold-dark',
  aset: 'bg-success/12 text-success',
  bukan: 'bg-ivory text-mocha',
};

export function KasPerAkun({ versi, onPerluCek }) {
  const [buka, setBuka] = useState(true);
  const [bulan, setBulan] = useState(() => todayStr().slice(0, 7));
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState('');

  useEffect(() => {
    let batal = false;
    api.get('/kas/per-akun', { params: { bulan } })
      .then((r) => {
        if (batal) return;
        const d = r.data || {};
        // Jawaban tidak lengkap (mis. mesin versi lama) tidak boleh merusak halaman Kas.
        setData({ biaya_usaha: 0, belanja_aset: 0, bukan_biaya: 0, total: 0, perlu_cek: 0, ...d,
          kelompok: Array.isArray(d.kelompok) ? d.kelompok : [] });
        setGalat('');
      })
      .catch((e) => { if (!batal) setGalat(apiError(e)); });
    return () => { batal = true; };
  }, [bulan, versi]);

  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="kas-per-akun">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => setBuka((v) => !v)} data-testid="kas-per-akun-toggle"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-mocha font-semibold">
          Pengeluaran per akun {buka ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <input type="month" value={bulan} onChange={(e) => e.target.value && setBulan(e.target.value)}
          data-testid="kas-per-akun-bulan"
          className="rounded-xl border border-input bg-ivory/50 px-3 py-1.5 text-xs outline-none" />
      </div>
      {galat && <p className="text-xs text-danger mt-2">{galat}</p>}
      {buka && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <div className="rounded-xl bg-brown-dark text-white p-3" data-testid="kas-per-akun-biaya">
              <p className="text-[10px] uppercase tracking-wider opacity-80">Biaya usaha</p>
              <p className="font-display text-lg mt-0.5">{formatIDR(data.biaya_usaha)}</p>
              <p className="text-[10px] opacity-80">operasional + karyawan</p>
            </div>
            <div className="rounded-xl bg-success/12 p-3" data-testid="kas-per-akun-aset">
              <p className="text-[10px] uppercase tracking-wider text-success">Belanja aset</p>
              <p className="font-display text-lg text-brown-dark mt-0.5">{formatIDR(data.belanja_aset)}</p>
              <p className="text-[10px] text-mocha">stok & peralatan, bukan biaya bulanan</p>
            </div>
            <div className="rounded-xl bg-ivory p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha">Bukan biaya</p>
              <p className="font-display text-lg text-brown-dark mt-0.5">{formatIDR(data.bukan_biaya)}</p>
              <p className="text-[10px] text-mocha">prive, utang, pinjaman, lainnya</p>
            </div>
            <div className="rounded-xl border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-mocha">Total uang keluar</p>
              <p className="font-display text-lg text-brown-dark mt-0.5" data-testid="kas-per-akun-total">{formatIDR(data.total)}</p>
            </div>
          </div>
          {data.perlu_cek > 0 && (
            <button type="button" onClick={onPerluCek} data-testid="kas-per-akun-cek"
              className="mt-3 w-full text-left rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 hover:bg-amber-100">
              ⚠️ <b>{data.perlu_cek} transaksi</b> akunnya perlu dicek. Ketuk untuk melihat, lalu betulkan lewat ikon pensil.
            </button>
          )}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs" data-testid="kas-per-akun-tabel">
              <tbody>
                {data.kelompok.filter((g) => g.jumlah > 0 && Array.isArray(g.akun)).map((g) => (
                  <FragmenKelompok key={g.kelompok} g={g} />
                ))}
                {data.total === 0 && (
                  <tr><td className="py-4 text-center text-mocha">Belum ada pengeluaran di bulan ini.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function FragmenKelompok({ g }) {
  return (
    <>
      <tr className="border-t border-border">
        <td colSpan={3} className="pt-3 pb-1.5">
          <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${WARNA[g.kelompok] || ''}`}>{g.kode} · {g.nama}</span>
        </td>
        <td className="pt-3 pb-1.5 text-right font-bold whitespace-nowrap">{formatIDR(g.total)}</td>
      </tr>
      {g.akun.map((a) => (
        <tr key={a.nama} className="text-ink">
          <td className="py-1 pr-2 text-mocha font-mono text-[10px] whitespace-nowrap w-10">{a.kode}</td>
          <td className="py-1 pr-2">{a.nama}{a.lama && <span className="ml-1 text-[10px] text-amber-700">(kategori lama)</span>}</td>
          <td className="py-1 pr-2 text-right text-mocha whitespace-nowrap">{a.jumlah}x</td>
          <td className="py-1 text-right whitespace-nowrap">{formatIDR(a.nilai)}</td>
        </tr>
      ))}
    </>
  );
}
